(() => {
  const STORAGE_KEY = 'dt_journey_context_v1';
  const PAGE = window.DTJourneyPage || {};

  const readContext = () => {
    try {
      return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}');
    } catch (error) {
      return {};
    }
  };

  const writeContext = (nextContext) => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(nextContext));
    } catch (error) {
      // Tracking should never affect shopping when storage is unavailable.
    }
  };

  const getCampaign = () => {
    const context = readContext();
    if (context.campaign) return context.campaign;

    const params = new URLSearchParams(window.location.search);
    const campaign = ['utm_source', 'utm_medium', 'utm_campaign']
      .map((key) => params.get(key))
      .filter(Boolean)
      .join('|');

    if (campaign) writeContext({ ...context, campaign });
    return campaign;
  };

  const track = (eventName, payload = {}) => {
    const eventPayload = {
      journey_version: 1,
      page_type: PAGE.pageType || '',
      page_path: PAGE.path || window.location.pathname,
      campaign: getCampaign(),
      ...payload
    };

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: eventName, ...eventPayload });
    } catch (error) {
      // Existing analytics must not be a dependency for the storefront.
    }

    try {
      const published = window.Shopify?.analytics?.publish?.(eventName, eventPayload);
      if (published && typeof published.catch === 'function') published.catch(() => {});
    } catch (error) {
      // Shopify analytics is optional on previews and older storefront contexts.
    }
  };

  const collectionPayload = (collection = PAGE.collection) => {
    if (!collection || !collection.handle) return {};
    return {
      collection_id: String(collection.id || ''),
      collection_handle: collection.handle,
      collection_title: collection.title || ''
    };
  };

  const storeProductDiscovery = (payload) => {
    const context = readContext();
    writeContext({ ...context, productDiscovery: payload });
  };

  const getPlacementContext = (form) => {
    const placementRoot = form.closest('[data-dt-journey-placement]');
    if (placementRoot) {
      return {
        source: placementRoot.dataset.dtJourneySource || placementRoot.dataset.dtJourneyPlacement,
        placement: placementRoot.dataset.dtJourneyPlacement,
        collection: placementRoot.dataset.dtJourneyCollection || ''
      };
    }

    if (PAGE.collection?.handle) {
      return {
        source: 'collection:' + PAGE.collection.handle,
        placement: 'collection-grid',
        collection: PAGE.collection.handle
      };
    }

    if (PAGE.product?.handle) {
      const discovery = readContext().productDiscovery;
      if (discovery && String(discovery.productId) === String(PAGE.product.id)) {
        return {
          source: discovery.source || 'product:' + PAGE.product.handle,
          placement: 'product-page',
          collection: discovery.collection || ''
        };
      }

      return {
        source: 'product:' + PAGE.product.handle,
        placement: 'pdp',
        collection: ''
      };
    }

    return { source: 'direct', placement: 'product-form', collection: '' };
  };

  const setLineProperty = (form, key, value) => {
    if (!value) return;

    const fieldName = 'properties[' + key + ']';
    const fields = form.querySelectorAll('input');
    let field = null;

    fields.forEach((input) => {
      if (input.getAttribute('data-dt-journey-field') === key) field = input;
      if (!field && input.getAttribute('name') === fieldName) field = input;
    });

    if (field && !field.hasAttribute('data-dt-journey-field')) return;

    if (!field) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.name = fieldName;
      field.setAttribute('data-dt-journey-field', key);
      form.appendChild(field);
    }

    field.value = value;
  };

  const addJourneyProperties = (form, context) => {
    setLineProperty(form, '_dt_source', context.source);
    setLineProperty(form, '_dt_placement', context.placement);
    setLineProperty(form, '_dt_collection', context.collection);
    setLineProperty(form, '_dt_campaign', getCampaign());
  };

  const isProductForm = (form) => {
    if (!(form instanceof HTMLFormElement)) return false;
    if (!form.querySelector('input[name="id"]')) return false;
    return Boolean(form.closest('product-form') || (form.getAttribute('action') || '').includes('/cart/add'));
  };

  const getCardPosition = (card, grid) => {
    if (!card || !grid) return '';
    return String(Array.prototype.indexOf.call(grid.querySelectorAll('product-info[data-product-id]'), card) + 1);
  };

  const onProductCardClick = (event) => {
    const link = event.target.closest('a[href*="/products/"]');
    if (!link) return;

    const card = link.closest('product-info[data-product-id]');
    const grid = link.closest('[data-dt-journey-collection]');
    const collectionHandle = grid?.dataset.dtJourneyCollection || PAGE.collection?.handle;
    if (!card || !collectionHandle) return;

    const payload = {
      source: 'collection:' + collectionHandle,
      placement: 'collection-grid',
      collection: collectionHandle,
      productId: card.dataset.productId || '',
      position: getCardPosition(card, grid)
    };

    storeProductDiscovery(payload);
    track('dt_item_selected', {
      product_id: payload.productId,
      source: payload.source,
      placement: payload.placement,
      collection_handle: collectionHandle,
      item_position: payload.position
    });
  };

  document.addEventListener('click', onProductCardClick, { passive: true });
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!isProductForm(form)) return;

    const variantId = form.querySelector('input[name="id"]')?.value || '';
    const productId = PAGE.product?.id || form.closest('product-info[data-product-id]')?.dataset.productId || '';
    const context = getPlacementContext(form);
    addJourneyProperties(form, context);
    track('dt_add_to_cart', {
      product_id: String(productId),
      variant_id: String(variantId),
      source: context.source,
      placement: context.placement,
      collection_handle: context.collection
    });
  }, true);

  if (PAGE.collection?.handle) {
    const payload = collectionPayload();
    track('dt_collection_viewed', {
      ...payload,
      source: 'collection:' + PAGE.collection.handle,
      placement: 'collection-page'
    });
  }

  if (PAGE.product?.id) {
    const context = getPlacementContext(document.body);
    track('dt_product_viewed', {
      product_id: String(PAGE.product.id),
      product_handle: PAGE.product.handle || '',
      source: context.source,
      placement: context.placement,
      collection_handle: context.collection
    });
  }

  window.DTJourney = { track };
})();
